import { useClerk } from "@clerk/tanstack-react-start";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import type { FormEvent } from "react";
import { useState } from "react";
import { useForm, FormProvider, useFormContext } from "react-hook-form";

import { AuthCard } from "~/components/auth/auth-card";
import { AuthFormError } from "~/components/auth/auth-form-error";
import { AuthShell } from "~/components/auth/auth-shell";
import { Button } from "~/components/ui/button";
import { FormField } from "~/components/ui/form-field";
import { Input } from "~/components/ui/input";
import {
  MultiStepForm,
  useMultiStepForm,
} from "~/components/ui/multi-step-form";
import { OtpInput } from "~/components/ui/otp-input";
import { getClerkErrorMessage } from "~/lib/clerk-errors";
import type { UserForgotPasswordForm } from "~/lib/schemas/user-auth";
import { UserForgotPasswordSchema } from "~/lib/schemas/user-auth";
import { createNoIndexMeta } from "~/lib/shared/meta";

// --- Step Components ---

const EmailStep = ({
  onNext,
  isProcessing,
  error,
}: {
  onNext: () => Promise<void>;
  isProcessing: boolean;
  error?: string;
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<UserForgotPasswordForm>();
  const { next } = useMultiStepForm();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await onNext();
      next();
    } catch {
      // Error is handled by the parent component, don't proceed to next step
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full shrink-0 space-y-8 px-1">
      <div className="space-y-2">
        <Link to="/login" className="text-sm text-gray-500 hover:text-gray-700">
          <span className="inline-flex items-center">
            <ChevronLeft /> Log In
          </span>
        </Link>
        <h2 className="text-xl font-medium">Forgot password</h2>
      </div>
      <div className="space-y-2">
        <FormField
          fullWidth
          label="Enter email or phone number"
          error={errors.email?.message}
        >
          <Input
            {...register("email")}
            type="email"
            placeholder="Enter email or phone number"
            error={errors.email}
            disabled={isProcessing}
          />
        </FormField>
      </div>
      <div id="clerk-captcha" className="w-full" />
      <Button
        type="submit"
        loading={isProcessing}
        disabled={isProcessing}
        variant="primary"
        className="mb-2 w-full"
      >
        Reset Password
      </Button>
      <AuthFormError message={error} />
    </form>
  );
};

const OtpStep = ({
  onNext,
  onResend,
  isProcessing,
  canResend,
  resendTimer,
  email,
  error,
  code,
  onOtpChange,
}: {
  onNext: (code: string) => Promise<void>;
  onResend: () => void;
  isProcessing: boolean;
  canResend: boolean;
  resendTimer: number;
  email: string;
  error: string;
  code: string[];
  onOtpChange: (code: string[]) => void;
}) => {
  const { next } = useMultiStepForm();

  const handleComplete = async (codeValue: string) => {
    try {
      await onNext(codeValue);
      next();
    } catch {
      // Error is handled by the parent component, don't proceed to next step
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await handleComplete(code.join(""));
  };

  return (
    <form onSubmit={handleSubmit} className="w-full shrink-0 px-1">
      <div className="space-y-2">
        <h1 className="font-heading text-3xl">Check your email</h1>
        <h3 className="text-text-secondary font-heading">
          {`We sent a code to ${email}. Enter 6 digit code that mentioned in the email`}
        </h3>
      </div>
      <div className="my-8 flex items-end gap-4 transition-opacity duration-300 ease-in-out">
        <OtpInput
          value={code}
          error={error}
          onChange={onOtpChange}
          onComplete={handleComplete}
          disabled={isProcessing}
        />
      </div>
      <Button
        type="submit"
        className="mb-2 w-full"
        loading={isProcessing}
        disabled={isProcessing || code.some((digit) => !digit.trim())}
        variant="primary"
      >
        Verify
      </Button>
      <Button
        type="button"
        variant="ghost"
        className="w-full tabular-nums"
        onClick={onResend}
        disabled={!canResend || isProcessing}
      >
        {canResend ? "Resend code" : `Resend code (${resendTimer}s)`}
      </Button>
      <AuthFormError message={error} className="mt-2" />
    </form>
  );
};

const NewPasswordStep = ({
  onNext,
  isProcessing,
  error,
}: {
  onNext: () => Promise<void>;
  isProcessing: boolean;
  error?: string;
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<UserForgotPasswordForm>();
  const { next } = useMultiStepForm();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await onNext();
      next();
    } catch {
      // Error is handled by the parent component, don't proceed to next step
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full shrink-0 space-y-8 px-1">
      <div className="space-x-2">
        <h1 className="font-heading text-3xl">Set a New Password</h1>
        <h3 className="text-text-secondary font-heading">
          Create a new password. Ensure it differs from previous ones for
          security
        </h3>
      </div>
      <div>
        <div className="space-y-2">
          <FormField
            fullWidth
            label="Create Password"
            error={errors.password?.message}
          >
            <Input
              {...register("password")}
              autoComplete="off"
              type="password"
              placeholder="Create Password"
              error={errors.password}
              disabled={isProcessing}
            />
          </FormField>
        </div>
        <div className="space-y-2">
          <FormField
            fullWidth
            label="Confirm Password"
            error={errors.password2?.message}
          >
            <Input
              {...register("password2")}
              autoComplete="off"
              type="password"
              placeholder="Confirm Password"
              error={errors.password2}
              disabled={isProcessing}
            />
          </FormField>
        </div>
      </div>
      <Button
        type="submit"
        className="mb-2 w-full"
        loading={isProcessing}
        disabled={isProcessing}
        variant="primary"
      >
        Update Password
      </Button>
      <AuthFormError message={error} />
    </form>
  );
};

export const Route = createFileRoute("/_auth/forgot-password/")({
  head: () => {
    const metaConfig = createNoIndexMeta(
      "Forgot Password – Dream Coach Match",
      "Reset your password to regain access to your Dream Coach Match account. Enter your email to receive password reset instructions.",
    );

    return {
      ...metaConfig,
      links: [
        {
          rel: "canonical",
          href: "https://dreamcoachmatch.com/forgot-password",
        },
      ],
    };
  },
  component: ForgotPasswordPage,
});

const ForgotPasswordComponent = () => {
  const form = useForm<UserForgotPasswordForm>({
    resolver: zodResolver(UserForgotPasswordSchema),
    defaultValues: {
      email: "",
      password: "",
      password2: "",
    },
  });
  const { watch, getValues } = form;

  const navigate = useNavigate();
  const clerk = useClerk();

  const [isProcessing, setIsProcessing] = useState(false);
  const [state, setState] = useState({
    code: ["", "", "", "", "", ""],
    error: "",
    resendTimer: 60,
    canResend: false,
  });

  const startResendTimer = () => {
    setState((prev) => ({
      ...prev,
      resendTimer: 60,
      canResend: false,
    }));

    const timer = setInterval(() => {
      setState((prev) => {
        const newTimer = prev.resendTimer - 1;
        if (newTimer <= 0) {
          clearInterval(timer);
          return { ...prev, resendTimer: 0, canResend: true };
        }
        return { ...prev, resendTimer: newTimer };
      });
    }, 1000);
  };

  const handleSendOtp = async (): Promise<void> => {
    const isValid = await form.trigger(["email"]);
    if (!isValid) {
      throw new Error("Form validation failed");
    }

    startResendTimer();
    setState((prev) => ({ ...prev, error: "" }));
    setIsProcessing(true);
    const emailAddress = getValues("email");

    try {
      if (!clerk?.client) {
        throw new Error("Clerk client not available");
      }

      const signInAttempt = await clerk.client.signIn.create({
        identifier: emailAddress,
      });

      // Find the reset_password_email_code factor to get the correct emailAddressId
      const resetPasswordFactor = signInAttempt.supportedFirstFactors?.find(
        (factor) => factor.strategy === "reset_password_email_code",
      );

      if (!resetPasswordFactor || !resetPasswordFactor.emailAddressId) {
        throw new Error("Reset password email factor not available");
      }

      await clerk.client.signIn.prepareFirstFactor({
        strategy: "reset_password_email_code",
        emailAddressId: resetPasswordFactor.emailAddressId,
      });

      setIsProcessing(false);
    } catch (error) {
      setIsProcessing(false);
      const message = getClerkErrorMessage(error, "Failed to send reset code");
      setState((prev) => ({ ...prev, error: message }));
      throw error; // Re-throw to prevent step progression
    }
  };

  const handleOtpComplete = async (code: string): Promise<void> => {
    setIsProcessing(true);

    try {
      if (!clerk?.client) {
        throw new Error("Clerk client not available");
      }

      const signInAttempt = await clerk.client.signIn.attemptFirstFactor({
        strategy: "reset_password_email_code",
        code,
      });

      // Check if OTP verification is successful
      // The status should be "needs_new_password" and firstFactorVerification should be "verified"
      if (
        signInAttempt.status !== "needs_new_password" ||
        signInAttempt.firstFactorVerification?.status !== "verified"
      ) {
        setIsProcessing(false);
        throw new Error("OTP verification failed");
      }

      setIsProcessing(false);
    } catch (error) {
      setIsProcessing(false);
      const message = getClerkErrorMessage(error, "Failed to verify OTP code");
      setState((prev) => ({ ...prev, error: message }));
      throw error; // Re-throw to prevent step progression
    }
  };

  const handleVerifyOtp = async (): Promise<void> => {
    const isValid = await form.trigger(["password", "password2"]);
    if (!isValid) {
      throw new Error("Form validation failed");
    }

    setIsProcessing(true);

    try {
      if (!clerk?.client) {
        throw new Error("Clerk client not available");
      }

      // Use resetPassword method after OTP verification is complete. A reset
      // signs every other session out: whoever else was signed in loses it.
      const resetResult = await clerk.client.signIn.resetPassword({
        password: getValues("password"),
        signOutOfOtherSessions: true,
      });

      if (resetResult.status === "complete" && resetResult.createdSessionId) {
        await clerk.setActive({ session: resetResult.createdSessionId });
        navigate({ to: "/dashboard" });
      }
    } catch (error) {
      const message = getClerkErrorMessage(error, "Failed to reset password");
      setState((prev) => ({ ...prev, error: message }));
      throw error; // Re-throw to prevent step progression
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOtpChange = (code: string[]) => {
    setState((prev) => ({ ...prev, code: [...code], error: "" }));
  };

  const watchedEmail = watch("email");

  return (
    <AuthShell>
      <AuthCard>
        <FormProvider {...form}>
          <MultiStepForm>
            <EmailStep
              onNext={handleSendOtp}
              isProcessing={isProcessing}
              error={state.error}
            />
            <OtpStep
              onNext={handleOtpComplete}
              onResend={handleSendOtp}
              isProcessing={isProcessing}
              canResend={state.canResend}
              resendTimer={state.resendTimer}
              email={watchedEmail}
              error={state.error}
              code={state.code}
              onOtpChange={handleOtpChange}
            />
            <NewPasswordStep
              onNext={handleVerifyOtp}
              isProcessing={isProcessing}
              error={state.error}
            />
          </MultiStepForm>
          <div className="mt-4 text-center text-sm">
            Remember your password?{" "}
            <Link
              to="/login"
              className="text-text-primary/100 font-medium hover:underline"
            >
              Log in
            </Link>
          </div>
        </FormProvider>
      </AuthCard>
    </AuthShell>
  );
};

function ForgotPasswordPage() {
  return <ForgotPasswordComponent />;
}
